import React, { useState, useEffect, useRef } from "react";
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Button, 
  TextField, 
  Alert, 
  Snackbar,
  Box,
  Typography,
  CircularProgress,useMediaQuery,useTheme
} from "@mui/material";
import RoomIcon from '@mui/icons-material/Room';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';

const GoogleMapModal = ({ open, handleClose, onLocationSelect, initialCenter, language, keywordsData }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState(initialCenter || { lat: 30.0444, lng: 31.2357 });
  const [locationName, setLocationName] = useState(initialCenter?.address || "");
  const [gpsEnabled, setGpsEnabled] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Check if GPS is enabled
  const checkGpsEnabled = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(false);
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        () => resolve(true),
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            resolve(false);
          } else {
            resolve(false);
          }
        },
        { timeout: 10000 }
      );
    });
  };

  // Get current location using GPS
  const getCurrentLocation = () => {
    setGpsLoading(true);
    
    if (!navigator.geolocation) {
      setSnackbar({
        open: true,
        message: language === 'ar' 
          ? 'المتصفح الخاص بك لا يدعم تحديد الموقع الجغرافي'
          : 'Your browser does not support geolocation',
        severity: 'error'
      });
      setGpsLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const newCenter = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        
        setCenter(newCenter);
        setGpsEnabled(true);
        
        if (map) {
          map.setCenter(newCenter);
          getAddress(newCenter.lat, newCenter.lng);
        }
        
        setSnackbar({
          open: true,
          message: language === 'ar' 
            ? 'تم تحديد موقعك بنجاح'
            : 'Your location has been detected',
          severity: 'success'
        });
        setGpsLoading(false);
      },
      (error) => {
        setGpsEnabled(false);
        setGpsLoading(false);
        
        let errorMessage = '';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = language === 'ar'
              ? 'تم رفض الوصول إلى الموقع. يرجى تمكين خدمات الموقع في إعدادات متصفحك'
              : 'Location permission denied. Please enable location services in your browser settings';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = language === 'ar'
              ? 'معلومات الموقع غير متوفرة'
              : 'Location information is unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = language === 'ar'
              ? 'انتهى وقت محاولة تحديد الموقع'
              : 'Location request timed out';
            break;
          default:
            errorMessage = language === 'ar'
              ? 'حدث خطأ في تحديد الموقع'
              : 'An error occurred while detecting location';
        }
        
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'warning'
        });
      },
      { 
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      }
    );
  };

  // Check GPS status when modal opens
  useEffect(() => {
    if (open) {
      const checkGps = async () => {
        const enabled = await checkGpsEnabled();
        setGpsEnabled(enabled);
        
        if (!enabled) {
          setSnackbar({
            open: true,
            message: language === 'ar'
              ? '⚠️ خدمات الموقع غير مفعلة. يرجى تمكين GPS للحصول على مواقع دقيقة'
              : '⚠️ Location services are disabled. Please enable GPS for accurate location detection',
            severity: 'warning'
          });
        }
      };
      checkGps();
    }
  }, [open, language]);

  useEffect(() => {
    if (map && initialCenter) {
      map.setCenter(initialCenter);
      setCenter(initialCenter);
      getAddress(initialCenter.lat, initialCenter.lng);
    }
  }, [map, initialCenter]);

  useEffect(() => {
    if (!open) return;

    const loadGoogleMaps = () => {
      if (!mapRef.current) return;
      if (!window.google || !window.google.maps) {
        console.error("Google Maps API is not loaded yet.");
        return;
      }
      
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: center,
        zoom: 15,
        disableDefaultUI: true,
      });

      newMap.addListener("dragend", () => {
        const newCenter = newMap.getCenter();
        setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
        getAddress(newCenter.lat(), newCenter.lng());
      });

      setMap(newMap);
    };

    if (!window.google || !window.google.maps) {
      var api = process.env.REACT_APP_MAP_API_KEY;
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places&language=${language}`;
      script.async = true;
      script.onload = loadGoogleMaps;
      document.body.appendChild(script);
    } else {
      setTimeout(loadGoogleMaps, 300);
    }

    return () => {
      setMap(null);
    };
  }, [open]);

  // Reverse Geocoding (LatLng → Address)
  const getAddress = (lat, lng) => {
    if (!window.google || !window.google.maps) {
      console.error("Google Maps API is not loaded yet.");
      return;
    }
  
    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat, lng };
    
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === "OK" && results[0]) {
        setLocationName(results[0].formatted_address);
      } else {
        setLocationName(language === 'ar' ? keywordsData.location_not_found_txt_ar : keywordsData.location_not_found_txt_en);
      }
    });
  };

  const handleConfirmLocation = () => {
    onLocationSelect({ lat: center.lat, lng: center.lng, address: locationName });
    localStorage.setItem('is_check_location','true');
    handleClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} 
       maxWidth="md" 
  fullWidth 
  fullScreen={isMobile}
  dir={language === 'ar' ? 'rtl' : 'ltr'}
  PaperProps={{
    sx: {
      backgroundColor: '#0D0D0D',
      background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
      border: '1px solid rgba(255, 215, 0, 0.2)',
      borderRadius: isMobile ? 0 : '24px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,215,0,0.1)',
      overflow: 'hidden',
      position: 'relative',
    }
  }}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RoomIcon sx={{ color: '#FFD700' }} />
              <Typography variant="h6">
                {language === "ar" ? "موقع التوصيل" : "Delivery Location"}
              </Typography>
            </Box>
            
            {/* GPS Status Indicator */}
            {gpsEnabled !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {gpsEnabled ? (
                  <>
                    <GpsFixedIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: '#4CAF50' }}>
                      {language === 'ar' ? 'GPS نشط' : 'GPS Active'}
                    </Typography>
                  </>
                ) : (
                  <>
                    <GpsOffIcon sx={{ color: '#FF9800', fontSize: 20 }} />
                    <Typography variant="caption" sx={{ color: '#FF9800' }}>
                      {language === 'ar' ? 'GPS غير نشط' : 'GPS Inactive'}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* GPS Warning Alert */}
          {gpsEnabled === false && (
            <Alert 
              severity="warning" 
              sx={{ 
                mb: 2, 
                borderRadius: '12px',
                backgroundColor: 'rgba(255, 152, 0, 0.1)',
                border: '1px solid #FF9800'
              }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={getCurrentLocation}
                  disabled={gpsLoading}
                  sx={{ color: '#FF9800' }}
                >
                  {gpsLoading ? <CircularProgress size={20} /> : (language === 'ar' ? 'تمكين' : 'Enable')}
                </Button>
              }
            >
              <Typography variant="body2">
                {language === 'ar'
                  ? '⚠️ خدمات الموقع غير مفعلة. قم بتمكين GPS للحصول على موقعك الحالي بدقة'
                  : '⚠️ Location services are disabled. Enable GPS to get your current location accurately'}
              </Typography>
            </Alert>
          )}

          {/* Get Current Location Button */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={gpsLoading ? <CircularProgress size={20} /> : <LocationSearchingIcon />}
            onClick={getCurrentLocation}
            disabled={gpsLoading}
            sx={{
              mb: 2,
              borderColor: '#FFD700',
              color: '#FFD700',
              '&:hover': {
                borderColor: '#FFE55C',
                backgroundColor: 'rgba(255, 215, 0, 0.1)'
              }
            }}
          >
            {gpsLoading 
              ? (language === 'ar' ? 'جاري تحديد الموقع...' : 'Detecting location...')
              : (language === 'ar' ? 'استخدام موقعي الحالي' : 'Use my current location')}
          </Button>

          {/* Search Bar for Showing Selected Location Name */}
          <TextField
            fullWidth
            value={locationName}
            variant="outlined"
            placeholder={language === "ar" ? "ابحث عن منطقة، شارع، معلم معروف..." : "Search for an area, street, landmark..."}
            disabled
            sx={{ mb: 2 }}
            InputProps={{
              sx: {
                backgroundColor: '#1A1A1A',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' }
              }
            }}
          />

          <div style={{ height: "400px", position: "relative", borderRadius: '12px', overflow: 'hidden' }}>
            <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -100%)",
              }}
            >
              <RoomIcon style={{ color: '#FFD700', fontSize: 35 }} />
            </div>
          </div>
          
          <Button
            fullWidth
            variant="contained"
            sx={{ 
              mt: 2, 
              backgroundColor: '#FFD700', 
              color: '#000000',
              '&:hover': { backgroundColor: '#FFE55C' },
              py: 1.5,
              fontWeight: 700
            }}
            onClick={handleConfirmLocation}
          >
            {language === "ar" ? "تأكيد موقع التوصيل" : "Confirm Delivery Location"}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            backgroundColor: '#0D0D0D',
            color: '#FFFFFF',
            border: `1px solid ${snackbar.severity === 'success' ? '#4CAF50' : snackbar.severity === 'warning' ? '#FF9800' : '#F44336'}`,
            borderRadius: '12px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GoogleMapModal;